import React from "react";
import { Link } from "react-router-dom";
import { Search, Banknote, CalendarCheck, Globe, Zap, ArrowRight, Clock } from "lucide-react";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";

const ICON_MAP = { banknote: Banknote, "calendar-check": CalendarCheck, globe: Globe, zap: Zap };

export default function MarketplacePage() {
  const { t, formatCurrency, lang } = useLang();
  const [items, setItems] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [cat, setCat] = React.useState("");

  const load = React.useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    const { data } = await api.get("/api/products", { params });
    setItems(data.items || []);
  }, [q, cat]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    api.get("/api/categories").then((r) => setCategories(r.data.items || []));
  }, []);

  return (
    <div>
      <Header />
      <Seo title={t("seo.marketplace.title")} description={t("seo.marketplace.desc")} path="/marketplace" />
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-8">
        <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("landing.products.eyebrow")}</div>
        <h1 className="font-display text-4xl sm:text-5xl text-white/95">{t("marketplace.title")}</h1>
        <p className="text-white/55 mt-3 max-w-2xl">{t("marketplace.subtitle")}</p>

        <div className="mt-8 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("marketplace.search")}
              className="lp-input pl-10"
              data-testid="marketplace-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <button
              className={`px-4 py-2 rounded-full border ${!cat ? "border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37]" : "border-white/10 text-white/60 hover:text-white"} text-sm`}
              onClick={() => setCat("")}
              data-testid="cat-all"
            >{t("marketplace.all")}</button>
            {categories.map((c) => (
              <button
                key={c}
                className={`px-4 py-2 rounded-full border whitespace-nowrap ${cat === c ? "border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37]" : "border-white/10 text-white/60 hover:text-white"} text-sm`}
                onClick={() => setCat(c)}
                data-testid={`cat-${c.toLowerCase()}`}
              >{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        {items.length === 0 && (
          <div className="lp-card p-12 text-center text-white/50">{t("marketplace.empty")}</div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => {
            const Icon = ICON_MAP[p.icon] || Zap;
            const isComingSoon = !!p.coming_soon;
            const notReady = !isComingSoon && !p.sale_ready;
            return (
              <Link key={p.id} to={`/products/${p.slug}`} className={`lp-card p-6 flex flex-col group ${isComingSoon ? "opacity-90" : ""}`} data-testid={`mp-card-${p.slug}`}>
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[#d4af37]/8 border border-[#d4af37]/25 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="lp-chip">{p.category}</span>
                    {isComingSoon && (
                      <span className="lp-chip gold" data-testid={`mp-coming-soon-${p.slug}`}>
                        <Clock className="w-3 h-3" /> {t("marketplace.coming_soon")}
                      </span>
                    )}
                    {notReady && <span className="lp-chip amber"><Clock className="w-3 h-3" /> {t("marketplace.pending_config")}</span>}
                  </div>
                </div>
                <h3 className="font-display text-xl text-white mt-5">{displayProductName((lang === "tr" && p.name_tr) || p.name)}</h3>
                <p className="text-white/50 text-sm mt-2 flex-1">{(lang === "tr" && p.description_tr) || p.description}</p>
                {isComingSoon ? (
                  <div className="mt-5 lp-glass p-3 text-xs text-white/60">
                    {t("api.detail.coming_soon_body")}
                  </div>
                ) : (
                  <>
                    <div className="mt-5 grid grid-cols-2 gap-2 text-center">
                      <div className="lp-glass p-2.5 border-[#d4af37]/30" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
                        <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">{t("marketplace.card.pro")}</div>
                        <div className="text-sm text-white/95 mt-1 font-medium">{formatCurrency(p.plans_summary?.pro?.price || 200, "USD")}<span className="text-white/40 text-[10px]">{t("landing.pricing.per_month")}</span></div>
                      </div>
                      <div className="lp-glass p-2.5" style={{ borderColor: "rgba(212,175,55,0.2)" }}>
                        <div className="text-[10px] uppercase tracking-widest text-white/60">{t("marketplace.card.ent")}</div>
                        <div className="text-sm text-white/95 mt-1 font-medium">{formatCurrency(500, "USD")}<span className="text-white/40 text-[10px]">{t("landing.pricing.per_month")}</span></div>
                      </div>
                    </div>
                    <div className="mt-2 text-[10px] text-white/40 text-center" data-testid={`mp-tax-${p.slug}`}>
                      {t("pricing.tax.suffix")}
                    </div>
                  </>
                )}
                <div className="mt-5 flex items-center justify-end text-sm text-[#d4af37] group-hover:translate-x-1 transition-transform">
                  {t("landing.products.explore")} <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            );
          })}
        </div>
        <p className="text-xs text-white/45 mt-8 max-w-3xl" data-testid="marketplace-tax-note">
          {t("pricing.tax.note")}
        </p>
      </section>

      <Footer />
    </div>
  );
}
